'use client';

// Consistency Rule · "Example · consistency limit 25%" (Figma 12071:59914
// desktop 792×499, 12471:7823 mobile 366×846). Before/after cards with a
// best-day share ring, three rows, a status bar and the formula chips.
// Values are illustrative and hardcoded.
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ExampleLabel, FigureFrame } from '../primitives';
import { CheckmarkIcon, WarningAltIcon } from '../Icons';

/** Donut ring: 104px, 7% stroke, arc from 12 o'clock clockwise (Figma "Anel"). */
function Ring({
  share,
  color,
  label,
  className,
}: {
  share: number;
  color: string;
  label: string;
  className?: string;
}) {
  const size = 104;
  const stroke = 7.28;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div
      data-ui="figure.consistencyExample.ring"
      className={cn('relative flex h-[13rem] w-[13rem] shrink-0 flex-col items-center justify-center', className)}
    >
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 h-full w-full" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeOpacity={0.12} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="butt"
          strokeDasharray={`${c * share} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <p className="relative mb-0 whitespace-nowrap font-geist text-[3rem] font-medium leading-[4rem]" style={{ color }}>
        {label}
      </p>
      <p className="relative mb-0 whitespace-nowrap font-geist text-[2rem] font-normal leading-[2.5rem] text-black/40">
        best day
      </p>
    </div>
  );
}

type Card = {
  key: string;
  share: number;
  shareLabel: string;
  color: string;
  title: ReactNode;
  rows: { label: string; value: string; tone?: 'green' }[];
  met: boolean;
  statusLabel: string;
  statusValue?: string;
};

const CARDS: Card[] = [
  {
    key: 'now',
    share: 1 / 3,
    shareLabel: '33%',
    color: '#ac0003',
    title: (
      <>
        Now
        <br />
        total $3,000
      </>
    ),
    rows: [
      { label: 'Biggest winning day', value: '$1,000' },
      { label: 'Minimum profit (÷ 25%)', value: '$4,000', tone: 'green' },
      { label: 'Current total profit', value: '$3,000' },
    ],
    met: false,
    statusLabel: 'Still missing',
    statusValue: '$1,000',
  },
  {
    key: 'after',
    share: 0.25,
    shareLabel: '25%',
    color: '#228e71',
    title: (
      <>
        After 2 more days
        <br />
        total $4,000
      </>
    ),
    rows: [
      { label: 'Biggest winning day', value: '$1,000' },
      { label: 'Minimum profit (÷ 25%)', value: '$4,000', tone: 'green' },
      { label: 'Current total profit', value: '$4,000' },
    ],
    met: true,
    statusLabel: 'Consistency met',
  },
];

function Chip({ children, tone = 'gray' }: { children: ReactNode; tone?: 'gray' | 'green' }) {
  return (
    <span
      className={cn(
        'inline-flex h-[3.5rem] shrink-0 items-center whitespace-nowrap rounded-[0.25rem] px-[1.25rem] font-geist text-[2rem] font-medium leading-[3rem]',
        tone === 'green' ? 'bg-[#228e71] text-white' : 'bg-[#f2f4f3] text-black',
      )}
    >
      {children}
    </span>
  );
}

export default function ConsistencyExample() {
  return (
    <FigureFrame data-ui="figure.consistencyExample" padding="p-[2rem] lg:p-[3rem]" className="gap-[2.5rem] [text-wrap:wrap]">
      <ExampleLabel>EXAMPLE · CONSISTENCY LIMIT 25%</ExampleLabel>

      <div data-ui="figure.consistencyExample.cards" className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-start">
        {CARDS.map((card) => (
          <div
            key={card.key}
            data-ui={`figure.consistencyExample.card-${card.key}`}
            className="flex w-full min-w-0 flex-col gap-[2rem] rounded-[0.25rem] bg-[#f2f4f3] p-[2.5rem] font-geist text-[2rem] leading-[3rem] lg:flex-1"
          >
            <div className="flex w-full items-start gap-[2rem] lg:items-center">
              <Ring share={card.share} color={card.color} label={card.shareLabel} />
              <p className="mb-0 min-w-0 flex-1 font-medium text-black">{card.title}</p>
            </div>

            <div className="flex w-full flex-col">
              {card.rows.map((row) => (
                <div
                  key={row.label}
                  className="flex w-full items-center justify-between border-b border-black/[0.06] py-[1.25rem] lg:px-[1.5rem]"
                >
                  <p className="mb-0 whitespace-nowrap font-normal text-black/[0.64]">{row.label}</p>
                  <p className={cn('mb-0 whitespace-nowrap font-medium', row.tone === 'green' ? 'text-[#228e71]' : 'text-black')}>
                    {row.value}
                  </p>
                </div>
              ))}
            </div>

            <div
              data-ui={`figure.consistencyExample.status-${card.key}`}
              className={cn(
                'flex h-[3rem] w-full items-center justify-between rounded-[0.25rem] px-[1.5rem] font-medium lg:h-[6rem]',
                card.met ? 'bg-[#e2f3ef] text-[#196551]' : 'bg-[#ac0003]/[0.08] text-[#ac0003]',
              )}
            >
              <span className="flex items-center gap-[1rem]">
                <span className="h-[2rem] w-[2rem] shrink-0">{card.met ? <CheckmarkIcon /> : <WarningAltIcon />}</span>
                <span className="whitespace-nowrap">{card.statusLabel}</span>
              </span>
              {card.statusValue && <span className="whitespace-nowrap">{card.statusValue}</span>}
            </div>
          </div>
        ))}
      </div>

      <div
        data-ui="figure.consistencyExample.formula"
        className="flex w-full flex-wrap items-center gap-[1rem] font-geist text-[2rem] font-normal leading-[3rem] text-black/40"
      >
        <span className="w-[8.75rem] whitespace-nowrap lg:w-auto">Needed =</span>
        <Chip>best day $1,000</Chip>
        <span>÷</span>
        <Chip>25%</Chip>
        <span>=</span>
        <Chip tone="green">$4,000 total profit</Chip>
      </div>
    </FigureFrame>
  );
}
